import type { CrudTableColumn } from 'src/router/types';

/**
 * Determines if a value should be displayed as JSON
 * @param value - The value to check
 * @param columnConfig - Optional column configuration
 * @returns true if the value should be displayed as JSON
 */
export function shouldDisplayAsJson(value: any, columnConfig?: CrudTableColumn): boolean {
  // Check if column format is explicitly set to json
  if (columnConfig?.format === 'json') {
    return true;
  }

  // Auto-detect JSON strings
  if (typeof value === 'string') {
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  }

  // Auto-detect complex objects (but not arrays or relations)
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    // Don't treat relations as JSON
    if ('id' in value && ('name' in value || 'title' in value)) {
      return false;
    }
    return true;
  }

  return false;
}

/**
 * Safely parses JSON and returns the parsed value or null
 * @param value - The value to parse
 * @returns Parsed JSON object or null if parsing fails
 */
export function safeJsonParse(value: any): any {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

/**
 * Formats JSON with proper indentation
 * @param value - The value to format
 * @returns Formatted JSON string
 */
export function formatJson(value: any): string {
  const parsed = safeJsonParse(value);
  if (parsed === null) return '';
  return JSON.stringify(parsed, null, 2);
}

/**
 * Truncates JSON string to specified length
 * @param value - The value to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated JSON string
 */
export function truncateJson(value: any, maxLength: number = 100): string {
  const jsonString = JSON.stringify(safeJsonParse(value));
  if (jsonString.length <= maxLength) {
    return jsonString;
  }
  return jsonString.substring(0, maxLength) + '...';
}